import { useContext, useMemo } from "react";
import { GlobalStateContext } from "../../../utils/GlobalStateContext";
import { useSelector } from "react-redux";
import { createAccessMap, hasPermission } from "../../../utils/permissions";
import {
  fetchErrorArea,
  fetchErrorAreaQuestionsAsArray,
  fetchErrorDescription,
} from "../../../utils/Constants";

const InternalErrorRcaDetails = () => {
  const { globalState } = useContext(GlobalStateContext);
  const eDetails = globalState?.eDetails || {};
  const rawAccess = useSelector((state) => state.auth.access);

  const accessMap = useMemo(() => {
    return createAccessMap(rawAccess);
  }, [rawAccess]);

  // flatten data (root + internalerrorrcaID)
  const internalErrorRcaDetailsInfo = {
    ...eDetails,
    ...(eDetails.internalerrorrcaID || {}),
    status: eDetails?.status,
    createdAt: eDetails?.createdAt,
    updatedAt: eDetails?.updatedAt,
    auditor: eDetails?.auditor?.name || "",
    agent: eDetails?.agent?.name || "",
    client: eDetails?.client?.name || "",
    department: eDetails?.department?.name || "",
    process: eDetails?.process?.name || "",
    auditForm: eDetails?.auditForm?.formName || "",
    errorDescription:
      fetchErrorDescription(eDetails) ||
      eDetails?.internalerrorrcaID?.errorDescription ||
      "-",
    errorArea:
      fetchErrorArea(eDetails) ||
      eDetails?.internalerrorrcaID?.errorArea ||
      "-",
  };

  const newinternalErrorRca = [
    hasPermission(
      accessMap,
      "qMSEscalationsIERCADetailView",
      "AUDITSDETAILVIEW",
    ) && {
      key: "auditDetails",
      title: "Audit Details",
      fields: [
        { key: "stage", label: "Stage" },
        { key: "auditor", label: "Auditor" },
        { key: "score", label: "Audit Score" },
        { key: "client", label: "Client" },
        { key: "department", label: "Department" },
        { key: "process", label: "Process" },
        { key: "auditForm", label: "Audit Form" },
        { key: "transaction_id", label: "Transaction" },
        {
          key: "transactionDuplicateReason",
          label: "Reason for duplicate Transaction ID",
        },
        { key: "status", label: "Status" },
        { key: "agent", label: "Agent" },
        { key: "auditBackDateReason", label: "Reason for Audit's back date" },
        { key: "insuredName", label: "Insured Name" },
        { key: "fatal", label: "Fatal" },
        { key: "notFatal", label: "Not Fatal" },
        { key: "transactionDate", label: "Transaction Date" },
        { key: "createdAt", label: "Created At" },
        { key: "updatedAt", label: "Updated At" },
      ],
    },

    hasPermission(
      accessMap,
      "qMSEscalationsIERCADetailView",
      "FORMDETAILVIEW",
    ) && {
      key: "formDetails",
      title: "Form Details",
      fields: [
        { key: "errorRaiseDate", label: "Error Raise Date" },
        { key: "feedbackdate", label: "Feedback Date" },
        { key: "errorDescription", label: "Error Description" },
        { key: "errorImpact", label: "Error Impact" },
        // {
        //   key: "errorInvestigationRemarks",
        //   label: "Error investigation remarks",
        // },
        { key: "errorStatus", label: "Error Status" },
        { key: "advisorShiftDuringError", label: "Advisor Shift During Error" },
        { key: "erroronweekdayweekend", label: "Error on Weekday/Weekend" },
        { key: "dayoftheweek", label: "Day of the Week" },
        { key: "agentTenure", label: "Agent Tenure" },
        { key: "feedbackStatus", label: "Feedback Status" },
        {
          key: "feedbackShared",
          label: "Feedback shared in how many hrs after accepting error",
        },
        { key: "repeatedError", label: "Repeated Error" },
        { key: "correctiveActionTaken", label: "Corrective Action Taken" },
        {
          key: "preventiveActionTaken",
          label: "Preventive Action Taken (Action Plan)",
        },
        { key: "errorcorrected", label: "Error corrected in the system" },
        { key: "valueoflossprevented", label: "$ value of Loss prevented" },
        { key: "errorArea", label: "Error Area" },
        { key: "errorType", label: "Error Type" },
        { key: "rcaLevelOne", label: "Root Cause Analysis - L1" },
        { key: "rcaLevelTwo", label: "Root Cause Analysis - L2" },
        { key: "rcaLevelThree", label: "Root Cause Analysis - L3" },
        { key: "rcaLevelFour", label: "Root Cause Analysis - L4" },
      ],
    },
  ];

  const errorAreaQuestions = useMemo(() => {
    try {
      const formContent =
        typeof eDetails?.formContent === "string"
          ? JSON.parse(eDetails.formContent)
          : eDetails?.formContent;

      return (formContent?.elements?.questions || []).filter(
        (question) =>
          String(question?.selectedValue || "")
            .trim()
            .toLowerCase() === "no",
      );
    } catch {
      return [];
    }
  }, [eDetails?.formContent]);

  const renderSection = (section) => (
    <div key={section.key} className="form-section">
      <h2 className="form-section-title">{section.title}</h2>
      <div className="cusClientEscalationDetailsWrap pb-16">
        {section?.fields?.map((field) => {
          const value =
            field.key === "fatal" || field.key === "notFatal"
              ? internalErrorRcaDetailsInfo["fatalNotFatalInfo"] &&
                JSON.parse(internalErrorRcaDetailsInfo["fatalNotFatalInfo"])?.[
                  field.key
                ]
              : internalErrorRcaDetailsInfo[field.key] || "-";

          let displayValue = value;

          // Special: format dates
          const dateKeys = [
            "errorRaiseDate",
            "transactionDate",
            "feedbackdate",
            "createdAt",
            "updatedAt",
          ];
          if (dateKeys.includes(field.key) && value) {
            displayValue =
              value != "-" ? new Date(value).toLocaleDateString() : "-";
          }

          return (
            <div key={field.key} className="form-item">
              <div className="form-label">{field.label}</div>
              <div className="form-value">
                {field.key === "errorArea" && errorAreaQuestions.length > 0 ? (
                  errorAreaQuestions.length > 1 ? (
                    <ul className="error-area-list">
                      {errorAreaQuestions.map((question, index) => (
                        <li key={index}>
                          {question.display_text}
                          {question?.meta?.critical === true &&
                            question?.selectedValue === "No" && (
                              <span className="reqred"> *</span>
                            )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span>
                      {errorAreaQuestions[0]?.display_text}
                      {errorAreaQuestions[0]?.meta?.critical === true &&
                        errorAreaQuestions[0]?.selectedValue === "No" && (
                          <span className="reqred"> *</span>
                        )}
                    </span>
                  )
                ) : (
                  displayValue
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="internalErrorRca details-container">
      {/* Left Column (Audit + Time Stamps) */}
      {renderSection(newinternalErrorRca[0])}

      {/* Right Column (Form Details) */}
      {renderSection(newinternalErrorRca[1])}
    </div>
  );
};
export default InternalErrorRcaDetails;
